import React from "react";
import { Link } from "react-router-dom";
import MarketingNav from "@/components/MarketingNav";
import GoogleReviews, { GoogleRatingBadge } from "@/components/GoogleReviews";
import ShowcaseGallery from "@/components/ShowcaseGallery";
import QuoteForm from "@/components/QuoteForm";
import CustomerSubmissions from "@/components/CustomerSubmissions";
import QuoteCTA from "@/components/QuoteCTA";
import SiteFooter from "@/components/SiteFooter";
import ServicePills from "@/components/ServicePills";
import BeforeAfterCarousel from "@/components/BeforeAfterCarousel";
import MobileCollapse from "@/components/MobileCollapse";
import Seo from "@/components/Seo";
import { BIZ, localBusinessJsonLd } from "@/data/site";
import { services, coreServices } from "@/data/services";

const SERVICE_AREAS = [
  "Goodlettsville",
  "Hendersonville",
  "Madison",
  "White House",
  "Greenbrier",
  "Cottontown",
  "Gallatin",
];
import {
  Phone,
  MapPin,
  ArrowRight,
  Leaf,
  Heart,
  Target,
  Clock,
  ShieldCheck,
  Settings,
  Handshake,
  MessageSquare,
  Calculator,
  Send,
  CheckCircle2,
  Wrench,
  CalendarCheck,
} from "lucide-react";

export default function LandingPage() {
  return (
    <div className="bg-paper min-h-screen" data-testid="landing-page">
      <Seo
        title={`Pressure Washing & Soft Washing in ${BIZ.city}, ${BIZ.stateShort}`}
        description={`Local, licensed & insured pressure washing and soft washing serving ${BIZ.serviceAreas.slice(0, 3).join(", ")} and surrounding ${BIZ.region}. Free instant estimate. Call ${BIZ.phone}.`}
        path="/"
        jsonLd={localBusinessJsonLd()}
      />
      <MarketingNav />

      {/* GET A QUOTE — Top of every page */}
      <QuoteCTA />

      {/* HERO */}
      <section className="relative pt-2 md:pt-4 pb-16 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-grain opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 animate-fade-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-50 border border-brand-100 text-brand-800 text-xs font-bold uppercase tracking-[0.2em] mb-5 md:mb-8">
                <span className="w-2 h-2 rounded-full bg-brand-500 animate-pulse" />
                Locally owned · Family operated
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-7xl font-extrabold tracking-tight text-stone-900 leading-[0.95]" style={{ fontFamily: "Outfit" }}>
                Professional pressure washing in{" "}
                <span className="text-electric-500 italic">{BIZ.city}, {BIZ.stateShort}.</span>
              </h1>
              <div
                data-testid="hero-tagline-strip"
                className="mt-5 md:mt-6 inline-flex flex-wrap items-center gap-x-4 gap-y-1 text-xs md:text-sm font-extrabold uppercase tracking-[0.28em] text-brand-700"
              >
                <span>Protect.</span>
                <span className="text-electric-500">Restore.</span>
                <span>Impress.</span>
              </div>
              <p className="mt-6 md:mt-8 text-base md:text-xl text-stone-600 leading-relaxed max-w-2xl">
                Local, licensed &amp; insured pressure washing and soft washing for homes and businesses across {BIZ.serviceAreas.slice(0, 3).join(", ")} and surrounding {BIZ.region}. Right pressure. Right detergent. Same-day estimates.
              </p>
              <div className="mt-6 md:mt-10 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3" data-testid="hero-cta-row">
                <Link
                  to="/pressure-washing-estimate-calculator/"
                  data-testid="hero-calc-btn"
                  className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-5 md:py-4 bg-brand-600 hover:bg-electric-500 text-white font-bold text-base rounded-full transition-all hover:-translate-y-0.5 shadow-lg active:scale-[0.98]"
                >
                  Get an instant estimate
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="#quote"
                  data-testid="hero-quote-btn"
                  className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-5 md:py-4 bg-white text-stone-900 font-bold text-base rounded-full border border-stone-200 hover:border-stone-300 hover:bg-stone-50 transition-all hover:-translate-y-0.5 active:scale-[0.98]"
                >
                  Request a free quote
                </a>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="hero-contact-row">
                <a
                  href={`tel:${BIZ.phoneTel}`}
                  data-testid="hero-call-btn"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand-100 hover:bg-brand-200 text-brand-800 font-bold text-sm transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  Call {BIZ.phone}
                </a>
                <a
                  href={BIZ.phoneSmsHref}
                  data-testid="hero-text-btn"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand-100 hover:bg-brand-200 text-brand-800 font-bold text-sm transition-colors"
                >
                  <MessageSquare className="w-4 h-4" />
                  Text us
                </a>
                <GoogleRatingBadge />
              </div>
            </div>

            <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "0.15s" }}>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-brand-100/50 to-blue-200/40 rounded-[3rem] blur-2xl" />
                <div className="relative aspect-[4/5] rounded-[2.5rem] overflow-hidden shadow-2xl border-4 border-white">
                  <img
                    src="https://images.unsplash.com/photo-1711098256664-d2ea62e6e376?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxwcmVzc3VyZSUyMHdhc2hpbmclMjBkcml2ZXdheSUyMGhvdXNlfGVufDB8fHx8MTc4MTI3OTQ1Nnww&ixlib=rb-4.1.0&q=85"
                    alt="Clean home exterior"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP — from flyer: Reliable & Trusted · Quality Service · Honest Work, Fair Prices */}
      <section
        data-testid="trust-strip"
        className="relative bg-brand-700 border-y-4 border-electric-500 py-8 md:py-10"
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(46,124,224,0.25),transparent_45%)] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4 text-white">
          <TrustBadge
            icon={ShieldCheck}
            title="Reliable & Trusted"
            subtitle="Licensed. Insured. On time."
          />
          <TrustBadge
            icon={Settings}
            title="Quality Service"
            subtitle="Right pressure. Right detergent. Every time."
            accented
          />
          <TrustBadge
            icon={Handshake}
            title="Honest Work, Fair Prices"
            subtitle="Upfront quotes. No surprises."
          />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <ServicePills />
      <section
        id="how-it-works"
        data-testid="how-it-works"
        className="py-12 md:py-24 bg-white border-b border-stone-100 scroll-mt-24"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-3 md:mb-4">
              How it works
            </div>
            <h2
              className="text-2xl md:text-4xl font-extrabold text-stone-900 tracking-tight leading-tight"
              style={{ fontFamily: "Outfit" }}
            >
              Estimate to shiny in four simple steps.
            </h2>
          </div>
          <div className="mt-8 md:mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6" data-testid="hiw-steps">
            <HiwStep
              n={1}
              icon={Calculator}
              title="Get an estimate"
              body="Use our online calculator or call us for a preliminary price in minutes."
            />
            <HiwStep
              n={2}
              icon={Send}
              title="Send photos or request a quote"
              body="Share a photo of the property so we can confirm access, condition, and final pricing."
            />
            <HiwStep
              n={3}
              icon={CheckCircle2}
              title="Confirm the final price"
              body="We finalize the scope, put it in writing, and lock in your date."
            />
            <HiwStep
              n={4}
              icon={CalendarCheck}
              title="Schedule the service"
              body="On the day of service, we protect your landscaping, wash, and walk you through the results."
            />
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              to="/pressure-washing-estimate-calculator/"
              data-testid="hiw-calc-btn"
              className="inline-flex items-center gap-2 rounded-full bg-brand-600 hover:bg-electric-500 text-white font-bold px-6 py-3 transition-all hover:-translate-y-0.5 shadow-lg"
            >
              <Calculator className="w-4 h-4" />
              Get my instant estimate
            </Link>
            <a
              href={`tel:${BIZ.phoneTel}`}
              className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white text-stone-900 hover:border-stone-300 font-bold px-6 py-3 transition-all hover:-translate-y-0.5"
            >
              <Phone className="w-4 h-4" />
              {BIZ.phone}
            </a>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-24 md:py-32 bg-white border-y border-stone-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4">Services &amp; pricing</div>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-stone-900 leading-tight" style={{ fontFamily: "Outfit" }}>
              Simple, upfront pricing.
            </h2>
            <p className="mt-6 text-lg text-stone-600 leading-relaxed">
              No surprises on the invoice. Just the right pressure, the right detergent,
              and a job done right.
            </p>
          </div>

          <div className="mt-10 md:mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {services.map((s, i) => {
              const Icon = s.icon;
              const hasPhoto = Boolean(s.afterPhoto);
              return (
                <Link
                  key={s.slug}
                  to={s.seoSlug ? `/${s.seoSlug}/` : `/services/${s.slug}`}
                  data-testid={`service-card-${i}`}
                  className="group rounded-3xl bg-stone-50 border border-stone-100 hover:bg-white hover:border-stone-200 hover:shadow-[0_20px_40px_rgb(0,0,0,0.06)] transition-all duration-300 hover:-translate-y-1 flex flex-col overflow-hidden"
                >
                  {hasPhoto ? (
                    <div className="relative aspect-[4/3] md:aspect-[16/10] overflow-hidden">
                      <img
                        src={s.afterPhoto}
                        alt={`After — ${s.seoTitle || s.title}`}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/5 to-transparent" />
                      <div className="absolute top-3 left-3 flex items-center gap-2">
                        <div className="w-10 h-10 rounded-xl bg-white/90 backdrop-blur-sm flex items-center justify-center shadow">
                          <Icon className="w-5 h-5 text-brand-700" />
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-widest rounded px-2 py-1 bg-brand-600 text-white shadow">
                          After
                        </span>
                      </div>
                      <div className="absolute bottom-3 right-3 rounded-xl bg-white/95 backdrop-blur-sm px-3 py-1.5 shadow-md text-right">
                        <div className="text-[9px] font-bold uppercase tracking-[0.14em] text-stone-500">
                          {s.priceLabel}
                        </div>
                        <div className="text-xl font-extrabold text-stone-900 leading-tight" style={{ fontFamily: "Outfit" }}>
                          ${s.priceFrom}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-8 md:p-10 pb-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-brand-100 group-hover:bg-brand-600 flex items-center justify-center transition-colors flex-shrink-0">
                          <Icon className="w-7 h-7 text-brand-700 group-hover:text-white transition-colors" />
                        </div>
                        <div className="text-right">
                          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500">
                            {s.priceLabel}
                          </div>
                          <div className="text-3xl font-extrabold text-stone-900 mt-1" style={{ fontFamily: "Outfit" }}>
                            ${s.priceFrom}
                          </div>
                          {s.priceDetail && (
                            <div className="text-[10px] font-semibold text-brand-700 mt-1 leading-tight max-w-[8rem] ml-auto">
                              {s.priceDetail}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="p-6 md:p-8 flex flex-col flex-1">
                    <h3 className="text-xl md:text-2xl font-bold text-stone-900 leading-tight" style={{ fontFamily: "Outfit" }}>
                      {s.title}
                      <sup className="text-brand-700 font-extrabold ml-1">*</sup>
                    </h3>
                    <p className="mt-3 text-stone-600 leading-relaxed text-sm md:text-base flex-1">
                      {s.shortDesc}
                    </p>
                    <span
                      data-testid={`service-cta-${i}`}
                      className="mt-5 inline-flex items-center justify-center md:justify-start gap-2 text-sm font-bold text-white md:text-brand-700 md:bg-transparent bg-brand-600 group-hover:bg-brand-700 md:group-hover:bg-transparent rounded-full md:rounded-none px-5 md:px-0 py-3 md:py-0 md:text-brand-700"
                    >
                      Learn more &amp; book
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>

          <p
            data-testid="services-footnote"
            className="mt-10 text-sm text-stone-500 italic max-w-3xl"
          >
            <span className="text-brand-700 font-bold not-italic">*</span> Extra services and
            add-ons are available and may be approved once on site.
          </p>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section
        id="service-areas"
        data-testid="service-areas"
        className="py-14 md:py-24 bg-stone-50 border-b border-stone-100 scroll-mt-24"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8 lg:gap-10 items-start">
            <div className="lg:max-w-md">
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-3 md:mb-4 flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5" />
                Service Areas
              </div>
              <h2
                className="text-2xl md:text-4xl font-extrabold tracking-tight text-stone-900 leading-tight"
                style={{ fontFamily: "Outfit" }}
              >
                Proudly serving Middle Tennessee.
              </h2>
              <p className="mt-3 md:mt-4 text-stone-600 leading-relaxed text-sm md:text-base">
                We service homes across seven neighboring communities. Not sure if you&apos;re inside our
                range? Give us a call &mdash; we&apos;ll tell you straight.
              </p>
              <a
                href="tel:+16155192629"
                className="mt-5 md:mt-6 inline-flex items-center gap-2 text-sm font-bold text-brand-700 hover:text-electric-500 transition-colors"
              >
                <Phone className="w-4 h-4" />
                (615) 519-2629
              </a>
            </div>

            {/* Mobile: tap-to-expand accordion listing the 7 cities (keeps the scroll short). */}
            <MobileCollapse
              testId="service-areas-mobile-collapse"
              heading="See all 7 service areas"
              previewLabel="Goodlettsville, Hendersonville, Madison, White House…"
            >
              <div className="grid grid-cols-2 gap-3" data-testid="service-areas-list-mobile">
                {SERVICE_AREAS.map((city) => (
                  <div
                    key={`m-${city}`}
                    className="px-4 py-3 rounded-xl bg-white border border-stone-200 flex items-center gap-3"
                  >
                    <div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center">
                      <MapPin className="w-4 h-4 text-brand-700" />
                    </div>
                    <div className="font-bold text-stone-900 text-sm" style={{ fontFamily: "Outfit" }}>
                      {city}
                    </div>
                  </div>
                ))}
              </div>
            </MobileCollapse>

            {/* Desktop: full visible grid. */}
            <div
              className="hidden md:grid grid-cols-2 md:grid-cols-3 gap-3"
              data-testid="service-areas-list"
            >
              {SERVICE_AREAS.map((city) => (
                <div
                  key={city}
                  data-testid={`service-area-${city.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group px-5 py-4 rounded-2xl bg-white border border-stone-200 hover:border-electric-500 hover:shadow-md transition-all flex items-center gap-3"
                >
                  <div className="w-9 h-9 rounded-xl bg-brand-100 group-hover:bg-electric-500 flex items-center justify-center flex-shrink-0 transition-colors">
                    <MapPin className="w-4 h-4 text-brand-700 group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <div
                      className="font-bold text-stone-900 leading-tight"
                      style={{ fontFamily: "Outfit" }}
                    >
                      {city}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mt-0.5">
                      Tennessee
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <BeforeAfterCarousel />
      <ShowcaseGallery />

      <GoogleReviews variant="dark" />

      {/* ABOUT — mission, backstory, materials */}      <section id="about" className="py-24 md:py-32 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-5">
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4">About us</div>
              <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-stone-900 leading-tight" style={{ fontFamily: "Outfit" }}>
                Local hands.
                <br />
                Honest work.
              </h2>
              <p className="mt-6 text-lg text-stone-600 leading-relaxed">
                Wynridge Exterior Cleaning is a single-owner studio based right here in
                Goodlettsville, Tennessee — built to deliver showroom-quality results to
                every driveway, patio, and home exterior we touch.
              </p>
              <div className="mt-8 inline-flex items-center gap-3 px-5 py-3 rounded-full bg-white border border-stone-200 shadow-sm" data-testid="about-location">
                <MapPin className="w-4 h-4 text-brand-700" />
                <span className="text-sm font-semibold text-stone-800">Goodlettsville, TN 37072</span>
              </div>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-5">
              <AboutCard
                icon={Target}
                eyebrow="Our mission"
                title="Quality local service"
                body="Preserving the integrity and curb appeal of every home we touch — with the same care we'd want for our own."
                testId="about-mission"
              />
              <AboutCard
                icon={Heart}
                eyebrow="Our story"
                title="Single-owner startup"
                body="Founded by one owner with a power washer and a promise: show up, do good work, treat neighbors like family."
                testId="about-story"
              />
              <AboutCard
                icon={Leaf}
                eyebrow="Materials on request"
                title="Eco-friendly option available"
                body="Standard detergents get the job done. On request, we'll switch to a plant-safe biodegradable blend at no extra charge — just let us know when you book."
                testId="about-materials"
              />
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE FORM */}
      <section id="quote" className="py-24 md:py-32 bg-paper">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4">
              Free estimate
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-stone-900 leading-tight" style={{ fontFamily: "Outfit" }}>
              Request a quote.
              <br />
              We'll call you back.
            </h2>
            <p className="mt-6 text-lg text-stone-600 leading-relaxed">
              Tell us a little about your home. We answer inquiries around the clock — usually within an hour, any hour.
              A no-pressure, upfront estimate. (No bots. A real neighbor.)
            </p>

            <div className="mt-12 space-y-5">
              {[
                {
                  icon: Phone,
                  label: "(615) 519-2629",
                  sub: "Give us a ring — we'll answer.",
                  href: "tel:+16155192629",
                },
                {
                  icon: Clock,
                  label: "Open 24 / 7",
                  sub: "Any hour, any day — call or request a quote",
                },
                {
                  icon: MapPin,
                  label: "Goodlettsville, TN 37072",
                  sub: "Serving the local community",
                },
              ].map((c) => {
                const Wrap = c.href ? "a" : "div";
                const wrapProps = c.href ? { href: c.href } : {};
                return (
                  <Wrap
                    key={c.label}
                    {...wrapProps}
                    data-testid={`contact-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="flex items-start gap-4 group"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-white border border-stone-200 flex items-center justify-center shadow-sm flex-shrink-0 group-hover:border-brand-200 transition-colors">
                      <c.icon className="w-5 h-5 text-brand-700" />
                    </div>
                    <div>
                      <div className="font-bold text-stone-900">{c.label}</div>
                      <div className="text-sm text-stone-500">{c.sub}</div>
                    </div>
                  </Wrap>
                );
              })}
            </div>
          </div>

          <QuoteForm />
        </div>
      </section>

      <CustomerSubmissions />

      <SiteFooter />
    </div>
  );
}

function HiwStep({ n, icon: Icon, title, body }) {
  return (
    <div
      data-testid={`hiw-step-${n}`}
      className="relative pl-16 md:pl-6 pr-4 md:pr-0 py-4 md:p-6 rounded-2xl border border-stone-200 bg-stone-50 hover:border-electric-500 hover:bg-white hover:shadow-lg transition-all group"
    >
      {/* Big step number — on mobile it's a left-side disc; on desktop it floats above the card */}
      <div className="absolute left-4 top-4 md:left-6 md:-top-4 w-9 h-9 md:w-9 md:h-9 rounded-full bg-brand-600 group-hover:bg-electric-500 text-white text-sm font-extrabold flex items-center justify-center transition-colors">
        {n}
      </div>
      {/* Icon — hidden on mobile (step number does the work), shown on desktop */}
      <div className="hidden md:flex mt-2 w-11 h-11 rounded-xl bg-white border border-stone-200 items-center justify-center">
        <Icon className="w-5 h-5 text-brand-700" />
      </div>
      <h3 className="md:mt-5 font-bold text-stone-900 text-base md:text-lg leading-tight" style={{ fontFamily: "Outfit" }}>{title}</h3>
      <p className="mt-1 md:mt-2 text-sm text-stone-600 leading-relaxed">{body}</p>
    </div>
  );
}


function TrustBadge({ icon: Icon, title, subtitle, accented }) {
  return (
    <div
      className="flex items-center gap-4 justify-center md:justify-start px-2"
      data-testid={`trust-badge-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div
        className={`w-12 h-12 md:w-14 md:h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${
          accented ? "bg-electric-500" : "bg-white/10 border border-white/20"
        }`}
      >
        <Icon
          className={`w-6 h-6 md:w-7 md:h-7 ${accented ? "text-white" : "text-electric-300"}`}
          strokeWidth={2.25}
        />
      </div>
      <div>
        <div
          className="text-sm md:text-base font-extrabold text-white uppercase tracking-wider leading-tight"
          style={{ fontFamily: "Outfit" }}
        >
          {title}
        </div>
        <div className="text-xs text-white/70 mt-0.5">{subtitle}</div>
      </div>
    </div>
  );
}


function AboutCard({ icon: Icon, eyebrow, title, body, testId }) {
  return (
    <div
      data-testid={testId}
      className="p-6 md:p-7 rounded-3xl bg-white border border-stone-200 hover:border-brand-200 hover:shadow-[0_20px_40px_rgb(0,0,0,0.05)] transition-all duration-300 hover:-translate-y-1"
    >
      <div className="w-12 h-12 rounded-2xl bg-brand-100 flex items-center justify-center">
        <Icon className="w-5 h-5 text-brand-700" />
      </div>
      <div className="mt-5 text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">
        {eyebrow}
      </div>
      <h3
        className="mt-1 text-lg font-bold text-stone-900 leading-tight"
        style={{ fontFamily: "Outfit" }}
      >
        {title}
      </h3>
      <p className="mt-3 text-sm text-stone-600 leading-relaxed">{body}</p>
    </div>
  );
}

